import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  advancement as advApi,
  catalog as catalogApi,
  dens as densApi,
  meetings as meetingsApi,
  scouts as scoutsApi,
  ROLE_LABELS,
} from '../api';
import { useUnits } from '../units';

export function Dashboard() {
  const { currentUnit, roles, hasRole, loading } = useUnits();
  const [counts, setCounts] = useState<{
    dens: number;
    scouts: number;
    adventures: number;
    upcoming: number;
    readyToAward: number;
  } | null>(null);

  useEffect(() => {
    setCounts(null);
    if (!currentUnit || !(hasRole('ADMIN') || hasRole('DEN_LEADER'))) return;
    Promise.all([
      densApi.list(currentUnit.id),
      scoutsApi.list(currentUnit.id),
      catalogApi.get(currentUnit.id).catch(() => ({ adventures: [] as unknown[] })),
      meetingsApi.list(currentUnit.id, 'upcoming').catch(() => [] as unknown[]),
      advApi.summaries(currentUnit.id).catch(() => [] as { readyToAward: boolean }[]),
    ])
      .then(([d, s, c, m, adv]) =>
        setCounts({
          dens: d.dens.length,
          scouts: s.scouts.length,
          adventures: c.adventures.length,
          upcoming: m.length,
          readyToAward: adv.filter((x) => x.readyToAward).length,
        }),
      )
      .catch(() => setCounts(null));
  }, [currentUnit, hasRole]);

  if (loading) return <p className="muted">Loading…</p>;

  if (!currentUnit) {
    return (
      <div className="panel">
        <h1>No unit yet</h1>
        <p className="muted">
          You’re not a member of any unit. A unit Admin needs to add you, or a Platform Admin needs
          to create one.
        </p>
      </div>
    );
  }

  return (
    <div className="panel">
      <h1>{currentUnit.name}</h1>
      <p className="muted">
        {currentUnit.type === 'PACK' ? 'Cub Scout pack' : 'Troop'} · your role
        {roles.length > 1 ? 's' : ''}: {roles.map((r) => ROLE_LABELS[r]).join(', ')}
      </p>

      <div className="tiles">
        {(hasRole('ADMIN') || hasRole('DEN_LEADER')) && (
          <>
            <div className="tile">
              <div className="tile-num">{counts?.scouts ?? '—'}</div>
              <div className="tile-label">Scouts</div>
              <Link to={`/units/${currentUnit.id}/roster`} className="tile-link">
                Roster →
              </Link>
            </div>
            <div className="tile">
              <div className="tile-num">{counts?.dens ?? '—'}</div>
              <div className="tile-label">Dens</div>
              <Link to={`/units/${currentUnit.id}/dens`} className="tile-link">
                Dens →
              </Link>
            </div>
            <div className="tile">
              <div className="tile-num">{counts?.upcoming ?? '—'}</div>
              <div className="tile-label">Upcoming meetings</div>
              <Link to={`/units/${currentUnit.id}/meetings`} className="tile-link">
                Meetings →
              </Link>
            </div>
            <div className="tile">
              <div className="tile-num">{counts?.adventures ?? '—'}</div>
              <div className="tile-label">Adventures</div>
              <Link to={`/units/${currentUnit.id}/catalog`} className="tile-link">
                Catalog →
              </Link>
            </div>
            <div className="tile">
              <div className="tile-num">{counts?.readyToAward ?? '—'}</div>
              <div className="tile-label">Ready to award</div>
              <Link to={`/units/${currentUnit.id}/roster`} className="tile-link">
                Roster →
              </Link>
            </div>
          </>
        )}
        {hasRole('ADMIN') && (
          <div className="tile">
            <div className="tile-label">Unit</div>
            <Link to={`/units/${currentUnit.id}/members`} className="tile-link">
              Members →
            </Link>
          </div>
        )}
      </div>

      <p className="muted small">
        Foundation + unit administration (SCO-11 / 5 / 6 / 12 / 13 / 16). Finance, meetings,
        advancement, messaging, and calendar are later tickets.
      </p>
    </div>
  );
}
